// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule, Location } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BackToSystemOverviewComponent } from '../../portal/components/backToSystemOverview/backToSystemOverview.component';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import {
  SparePartsTab,
  SparePartsTabsComponent,
} from '../spare-parts-tabs/spare-parts-tabs.component';
import { ServiceTicketsComponent } from '../service-tickets/service-tickets.component';
import { ServicePartsComponent } from '../service-parts/service-parts.component';
import { RouteService } from '../../portal/services/routeService.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { StatusColorService } from '../services/status-color.service';
import { APICallGateComponent } from '../../shared/components/api-call-gate.component';
import {
  SpOperationalState,
  SpPartStatus,
  SpTicketStatus,
  SpTicketType,
} from '../enum/spare-parts-enums';
import { SpFilterModel } from '../model/sp-filter-model';
import { SpModalFilter, SpModalFilters } from '../model/sp-modal-filters';
import { SparePartsFilterComponent } from '../spare-parts-filter/spare-parts-filter.component';
import {
  DATE_DIVIDER,
  ISO_DATE_FORMAT,
} from '../spare-parts-datepicker/spare-parts-datepicker.component';
import { SparePartsOverviewService } from '../services/spare-parts-overview.service';
import { WidgetsServicePartsComponent } from '../widgets-service-parts/widgets-service-parts.component';
import { WidgetModel } from '../model/sp-widget-model';
import moment from 'moment';
import { SpTicketModel } from '../interfaces/sp-ticket-model';
import { SpPartsModel } from '../interfaces/sp-parts-model';
import { AmountConsumedGraphComponent } from '../amount-consumed-graph/amount-consumed-graph.component';
import { SpSortEventModel } from '../model/sp-sort-event-model';
import { HighestConsumedGraphComponent } from '../highest-consumed-graph/highest-consumed-graph.component';
import { ChartsService } from '../services/charts-data.service';

export type SpFilterSource = 'dateRange' | 'search-and-modal';

@Component({
  selector: 'app-spare-parts-page',
  standalone: true,
  templateUrl: './spare-parts-page.component.html',
  styleUrl: './spare-parts-page.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    TranslateModule,
    CommonModule,
    BackToSystemOverviewComponent,
    SparePartsTabsComponent,
    ServiceTicketsComponent,
    ServicePartsComponent,
    SparePartsFilterComponent,
    APICallGateComponent,
    WidgetsServicePartsComponent,
    AmountConsumedGraphComponent,
    HighestConsumedGraphComponent,
  ],
})
export class SparePartsPageComponent {
  protected readonly TAB_SERVICE_TICKETS: SparePartsTab = 'serviceTickets';
  protected readonly TAB_SERVICE_PARTS: SparePartsTab = 'serviceParts';
  protected readonly selectedTab = signal<SparePartsTab>(
    this.TAB_SERVICE_TICKETS,
  );

  protected readonly SRC_DATE_RANGE: SpFilterSource = 'dateRange';
  protected readonly SRC_INPUT_MODAL: SpFilterSource = 'search-and-modal';
  protected filterSourceTickets: SpFilterSource = this.SRC_DATE_RANGE;
  protected filterSourceParts: SpFilterSource = this.SRC_DATE_RANGE;

  protected readonly ticketsService = inject(SparePartsOverviewService);
  protected readonly chartDataService = inject(ChartsService);

  private readonly filtersToServiceTickets = new SpModalFilters({
    operationalState: new SpModalFilter(
      'spareParts.operationalState',
      [
        SpOperationalState.All,
        SpOperationalState.NoImpact,
        SpOperationalState.PartiallyDown,
        SpOperationalState.TotalDown,
      ],
      SpOperationalState.All,
    ),
    partStatus: new SpModalFilter(
      'spareParts.partStatus',
      [SpPartStatus.All, SpPartStatus.Consumed, SpPartStatus.Ordered],
      SpPartStatus.All,
    ),
  });

  private readonly filtersToServiceParts = new SpModalFilters({
    partStatus: new SpModalFilter(
      'spareParts.partStatus',
      [SpPartStatus.All, SpPartStatus.Consumed, SpPartStatus.Ordered],
      SpPartStatus.All,
    ),
    ticketStatus: new SpModalFilter(
      'spareParts.ticketStatus',
      [SpTicketStatus.All, SpTicketStatus.Closed, SpTicketStatus.Open],
      SpTicketStatus.All,
    ),
    ticketType: new SpModalFilter(
      'spareParts.ticketType',
      [
        SpTicketType.All,
        SpTicketType.ServiceTicket,
        SpTicketType.Maintenance,
        SpTicketType.Update,
      ],
      SpTicketType.All,
    ),
  });

  public serviceTicketsFilter = signal(
    new SpFilterModel('', this.filtersToServiceTickets),
  );

  public servicePartsFilter = signal(
    new SpFilterModel('', this.filtersToServiceParts),
  );

  public ticketFilter = computed(() => {
    const tab = this.selectedTab();
    if (tab === this.TAB_SERVICE_PARTS) {
      return this.servicePartsFilter();
    }
    return this.serviceTicketsFilter();
  });

  public highestPartsData$ =
    this.chartDataService.getHighestConsumedPartsData$.pipe(
      map((res) => res.parts),
    );

  public amountConsumeData$ =
    this.chartDataService.getAmountConsumedPartsData$.pipe(
      map((res) => res.data),
    );

  protected readonly allServiceTickets$ =
    this.ticketsService.getServiceTickets$.pipe(
      map((tickets) =>
        (tickets ?? []).map((ticket) => ({
          ...ticket,
          statusColor: this.statusColorService.statusColor(
            ticket.operationalState as SpOperationalState,
          ),
        })),
      ),
    );

  private readonly ticketsFilterChange$ = new BehaviorSubject<SpFilterModel>(
    this.serviceTicketsFilter(),
  );

  private readonly partsFilterChange$ = new BehaviorSubject<SpFilterModel>(
    this.servicePartsFilter(),
  );

  private readonly ticketsSortChange$ = new BehaviorSubject<SpSortEventModel>({
    property: undefined,
    isDescending: false,
  });

  private readonly partsSortChange$ = new BehaviorSubject<SpSortEventModel>({
    property: undefined,
    isDescending: false,
  });

  protected readonly dateRange$ = new BehaviorSubject<string>('');

  protected readonly dateRangeSignal = toSignal(this.dateRange$);

  protected readonly is3YearPeriod = computed(() => {
    const localDateRange = this.dateRangeSignal();
    if (localDateRange) {
      const [currStart, currEnd] = localDateRange.split(DATE_DIVIDER);
      const { start, end } = this.initDateRangeObj(3);

      if (currStart <= start && currEnd >= end) {
        return true;
      }
    }

    return false;
  });

  private firstDateRangeControl: boolean = true;
  protected readonly isFirstDateRange = computed(() => {
    const localDateRange = this.dateRangeSignal();
    if (localDateRange) {
      const { start, end } = this.initDateRangeObj();
      const [currStart, currEnd] = localDateRange.split(DATE_DIVIDER);

      const result =
        currStart === start && currEnd === end && this.firstDateRangeControl;
      if (result) {
        this.firstDateRangeControl = false;
      }

      return result;
    }

    return false;
  });

  protected readonly filteredTickets$ = combineLatest({
    dateRange: this.dateRange$,
    filter: this.ticketsFilterChange$,
    tickets: this.allServiceTickets$,
    sortChange: this.ticketsSortChange$,
  }).pipe(
    map(({ dateRange, filter, tickets, sortChange }) => {
      if (dateRange) {
        let creationDate: string | undefined | null;
        const [start, end] = dateRange.split(DATE_DIVIDER);
        const filtered = tickets.filter((ticket) => {
          creationDate = ticket.creationDate ?? '';
          if (!creationDate) {
            return true;
          }

          return (
            `${start}T00:00:00` <= creationDate &&
            creationDate <= `${end}T23:59:59`
          );
        });

        return { filter, tickets: filtered, sortChange };
      }

      return { filter, tickets, sortChange };
    }),
    map(({ filter, tickets, sortChange }) => {
      const searchInput = filter.searchInput.toLocaleLowerCase();
      const filtered = tickets.filter((ticket) => {
        return (
          String(ticket.ticketNumber)
            .toLocaleLowerCase()
            .includes(searchInput) ||
          String(ticket.description).toLocaleLowerCase().includes(searchInput)
        );
      });
      return { filter, tickets: filtered, sortChange };
    }),
    map(({ filter, tickets, sortChange }) => {
      let filtered = tickets;
      let selectedValue: string = '';
      let modalFilter: SpModalFilter;
      for (const key of Object.keys(filter.modalFilters)) {
        modalFilter = filter.modalFilters[
          key as keyof SpModalFilters
        ] as SpModalFilter;
        selectedValue = modalFilter.selectedValue;
        filtered = filtered.filter((ticket) => {
          if (selectedValue === 'All') {
            return true;
          }
          const tValue = ticket[key as keyof SpTicketModel];
          const ticketValue = String(tValue).toLowerCase();
          const selected = String(selectedValue).toLowerCase();
          return ticketValue === selected;
        });
      }
      return { filtered, sortChange };
    }),
    map(({ filtered, sortChange }) => {
      if (sortChange.property) {
        filtered.sort((a, b) => {
          const aValue =
            a[sortChange.property as keyof Omit<SpTicketModel, 'parts'>];
          const bValue =
            b[sortChange.property as keyof Omit<SpTicketModel, 'parts'>];
          return (
            String(aValue)
              .toLocaleLowerCase()
              .localeCompare(String(bValue).toLocaleLowerCase()) *
            (sortChange.isDescending ? -1 : 1)
          );
        });
      }

      return filtered;
    }),
  );

  protected ticketCountDisplay$ = combineLatest({
    filtered: this.filteredTickets$,
    allTickets: this.allServiceTickets$,
  }).pipe(
    map(({ filtered, allTickets }) => {
      const filteredCount = filtered?.length ?? 0;
      const allTickesCount = allTickets?.length ?? 0;

      return `(${filteredCount}/${allTickesCount})`;
    }),
  );

  public readonly allServiceParts$ = this.ticketsService.getServiceParts$;

  protected readonly filteredParts$ = combineLatest({
    dateRange: this.dateRange$,
    filter: this.partsFilterChange$,
    parts: this.allServiceParts$,
    sortChange: this.partsSortChange$,
  }).pipe(
    map(({ dateRange, filter, parts, sortChange }) => {
      if (dateRange) {
        let creationDate: string | undefined;
        const [start, end] = dateRange.split(DATE_DIVIDER);
        const filtered = parts.filter((part) => {
          creationDate = part.creationDate;
          if (!creationDate) {
            return true;
          }

          return (
            `${start}T00:00:00` <= creationDate &&
            creationDate <= `${end}T23:59:59`
          );
        });

        return { filter, parts: filtered, sortChange };
      }

      return { filter, parts, sortChange };
    }),
    map(({ filter, parts, sortChange }) => {
      const searchInput = filter.searchInput.toLocaleLowerCase();
      const filtered = parts.filter((part) => {
        return (
          String(part.ticketNumber).toLocaleLowerCase().includes(searchInput) ||
          String(part.partNumber).toLocaleLowerCase().includes(searchInput) ||
          String(part.quantity).toLocaleLowerCase().includes(searchInput) ||
          String(part.partDescription).toLocaleLowerCase().includes(searchInput)
        );
      });
      return { filter, parts: filtered, sortChange };
    }),
    map(({ filter, parts, sortChange }) => {
      let filtered = parts;
      let selectedValue: string = '';
      let modalFilter: SpModalFilter;
      for (const key of Object.keys(filter.modalFilters)) {
        modalFilter = filter.modalFilters[
          key as keyof SpModalFilters
        ] as SpModalFilter;
        selectedValue = modalFilter.selectedValue;
        filtered = filtered.filter((part) => {
          if (selectedValue === 'All') {
            return true;
          }
          const pValue = part[key as keyof SpPartsModel];
          const partValue = String(pValue).toLowerCase();
          const selected = String(selectedValue).toLowerCase();
          return partValue === selected;
        });
      }
      return { filtered, sortChange };
    }),
    map(({ filtered, sortChange }) => {
      if (sortChange.property) {
        filtered.sort((a, b) => {
          const aValue = a[sortChange.property as keyof SpPartsModel];
          const bValue = b[sortChange.property as keyof SpPartsModel];
          return (
            String(aValue)
              .toLocaleLowerCase()
              .localeCompare(String(bValue).toLocaleLowerCase()) *
            (sortChange.isDescending ? -1 : 1)
          );
        });
      }

      return filtered;
    }),
  );

  protected readonly widgetsValues$ = this.filteredParts$.pipe(
    map((parts) => {
      const ordered = SpPartStatus.Ordered.toLocaleLowerCase();
      const consumed = SpPartStatus.Consumed.toLocaleLowerCase();
      let stats = {
        consumed: 0,
        ordered: 0,
        mostReplaced: new Map<string, { part: SpPartsModel; count: number }>(),
      };

      let quantity = 0;
      stats = parts.reduce((acc, part) => {
        quantity = Number(part.quantity ?? 0);
        if (consumed === String(part.partStatus).toLocaleLowerCase()) {
          acc.consumed += quantity;
        } else if (ordered === String(part.partStatus).toLocaleLowerCase()) {
          acc.ordered += quantity;
        }

        if (consumed === String(part.partStatus).toLocaleLowerCase()) {
          const partNumber = String(part.partNumber);
          const partValue = acc.mostReplaced.get(partNumber);
          if (!partValue) {
            acc.mostReplaced.set(partNumber, { part, count: 1 });
          } else {
            partValue.count++;
          }
        }

        return acc;
      }, stats);

      const mostReplaced = Array.from(stats.mostReplaced.values()).sort(
        (a, b) => b.count - a.count,
      );

      return [
        {
          icon: 'utilities',
          header: 'spareparts.partsConsumed',
          count: stats.consumed,
        },
        {
          icon: 'delivery',
          header: 'spareparts.partsOrdered',
          count: stats.ordered,
        },
        {
          icon: 'scanner',
          header: 'spareparts.mostReplacedPart',
          subheader: mostReplaced[0]?.part?.partDescription,
          count: mostReplaced[0]?.count,
        },
      ] as WidgetModel[];
    }),
  );

  private readonly currentSystem = toSignal(this.routeService.currentSystem$);

  private readonly allServiceTicketsSignal = toSignal(this.allServiceTickets$);
  private readonly allServicePartsSignal = toSignal(this.allServiceParts$);

  protected readonly disableFilters = computed(() => {
    const tab = this.selectedTab();
    if (tab === this.TAB_SERVICE_PARTS) {
      return this.allServicePartsSignal()?.length === 0;
    } else {
      return this.allServiceTicketsSignal()?.length === 0;
    }
  });

  public constructor(
    private readonly routeService: RouteService,
    private readonly statusColorService: StatusColorService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
  ) {
    this.updateSelectedTab();

    const { start, end } = this.initDateRangeObj();
    this.requestTickets(start, end);

    this.dateRange$.next(`${start}${DATE_DIVIDER}${end}`);

    this.requestChartsData();

    effect(() => {
      const parent = this.activatedRoute.snapshot.parent?.url.join('/');
      if (parent) {
        this.location.go(`/${parent}/spareParts/${this.selectedTab()}`);
      }
    });
  }

  private updateSelectedTab(): void {
    const tab = this.activatedRoute.snapshot.paramMap.get('tab');
    if (tab && ['serviceTickets', 'serviceParts'].includes(tab)) {
      this.selectedTab.set(tab as SparePartsTab);
    } else {
      this.selectedTab.set('serviceTickets');
      const parent = this.activatedRoute.snapshot.parent?.url.join('/');
      if (parent) {
        this.location.go(`/${parent}/spareParts/serviceTickets`);
      }
    }
  }

  private requestTickets(start: string, end: string) {
    const sysDetails = this.currentSystem();
    if (sysDetails) {
      this.ticketsService.getSparePartsOverview.request({
        MaterialNumber: sysDetails.systemMaterialNumber,
        SerialNumber: sysDetails.systemSerialNumber,
        StartDate: start,
        EndDate: end,
      });
    }
  }

  private requestChartsData(): void {
    const sysDetails = this.currentSystem();
    if (sysDetails) {
      this.chartDataService.getHighestConsumedParts.request({
        materialNumber: sysDetails.systemMaterialNumber,
        serialNumber: sysDetails.systemSerialNumber,
      });
      this.chartDataService.getAmountConsumedParts.request({
        SerialNumber: sysDetails.systemSerialNumber,
        MaterialNumber: sysDetails.systemMaterialNumber,
      });
    }
  }

  protected onDateRangeChange(dateRange: string): void {
    this.filterSourceTickets = this.SRC_DATE_RANGE;
    this.filterSourceParts = this.SRC_DATE_RANGE;
    this.updateRequest(dateRange);
    this.dateRange$.next(dateRange);
  }

  protected onFilterChange(): void {
    if (this.selectedTab() === this.TAB_SERVICE_PARTS) {
      this.filterSourceParts = this.SRC_INPUT_MODAL;
      this.partsFilterChange$.next(this.servicePartsFilter());
    } else if (this.selectedTab() === this.TAB_SERVICE_TICKETS) {
      this.filterSourceTickets = this.SRC_INPUT_MODAL;
      this.ticketsFilterChange$.next(this.serviceTicketsFilter());
    }
  }

  private updateRequest(dateRange: string): void {
    const lastParams = this.ticketsService.getSparePartsOverview.lastParams;
    if (dateRange && lastParams) {
      const { StartDate, EndDate } = lastParams;
      const [start, end] = dateRange.split(DATE_DIVIDER);

      if (start < StartDate || end > EndDate) {
        this.requestTickets(start, end);
      }
    }
  }

  private initDateRangeObj(years: number = 1) {
    const start = moment.utc().subtract(years, 'years').format(ISO_DATE_FORMAT);
    const end = moment.utc().format(ISO_DATE_FORMAT);
    return { start, end };
  }

  protected onTicketsSortChange(change: SpSortEventModel): void {
    this.ticketsSortChange$.next(change);
  }

  protected onPartsSortChange(change: SpSortEventModel): void {
    this.partsSortChange$.next(change);
  }
}
